import { AttributeValue, Span, SpanStatusCode, trace } from '@opentelemetry/api';
import { sanitizeSpanAttributes } from './sanitizeSpanAttributes';
import { log } from '@domgen/shared/src/utils/log';

/**
 * @description Enhances the currently active OpenTelemetry span with additional attributes and error status if applicable.
 *
 * This utility function allows you to add contextual information to the active span, which can be invaluable for debugging and monitoring.
 * It takes a namespace to prefix the attribute keys, a data object containing key-value pairs to be added as attributes, and an optional isError flag to set the span status to error.
 * @param {string} namespace - A string to prefix all attribute keys, helping to categorize them.
 * @param {Record<string, AttributeValue>} data - An object containing key-value pairs to be added as attributes to the span. Values must be of type AttributeValue.
 * @param {boolean} [isError=false] - An optional flag indicating whether the span should be marked with an error status. Defaults to false.
 * @remarks
 * - If there is no active span, the function will simply return without performing any operations.
 * - Attribute keys will be prefixed with the provided namespace to avoid collisions and improve organization in telemetry data.
 * - Setting isError to true will mark the span's status as error, which can help in filtering and analyzing failed operations in your telemetry backend.
 * - Ensure you are not logging PII or sensitive information in the attributes, as they will be sent to your telemetry backend.

 * @example
 * // Example usage within a function that has an active span
 *
 * ````typescript
 * function fetchData() {
 *   const data = { userId: '12345', operation: 'fetchData' };
 *   enhanceSpan('myApp', { ...data });
 *
 *   // If an error occurs
 *   try {
 *     // some code that may throw an error
 *   } catch (error) {
 *     enhanceSpan('myApp', { errorMessage: error.message }, true);
 *   }
 * }
 * ````
 */
export const enhanceSpan = (
  namespace: string,
  data: Record<string, AttributeValue>,
  isError: boolean = false,
): void => {
  const span = trace.getActiveSpan();

  if (span) {
    log.debug({
      message: `Enhancing span ${span} with attributes:`,
      data: { namespace, data, isError },
    });
    const safeAttributes = sanitizeSpanAttributes(data);

    for (const [key, value] of Object.entries(safeAttributes)) {
      span.setAttribute(`${namespace}.${key}`, value);
    }

    if (isError) {
      span.setStatus({ code: SpanStatusCode.ERROR });
    }
  }
};

export const recordSpanError = (span: Span | undefined, error: unknown): void => {
  if (!span) {
    return;
  }

  if (error instanceof Error) {
    span.recordException(error);
    span.setAttribute('exception.message', error.message);
    span.setAttribute('exception.name', error.name);
  } else {
    span.recordException({ name: 'UnknownError', message: String(error) });
    span.setAttribute('exception.message', String(error));
  }

  span.setStatus({ code: SpanStatusCode.ERROR });
};
